import api from "./api";
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";
import { handleApiCall } from "../utils/apiResponseHandler";

export type ChatContextType = "general" | "underwriting" | "carrier_mapping" | "custom";

export interface CreateSessionRequest {
  contextType: ChatContextType;
}

export interface CreateSessionResponse {
  sessionId: string;
  contextType: ChatContextType;
  createdAt: string;
  message?: string;
}

export interface SendMessageRequest {
  message: string;
  sessionId: string;
  contextType?: ChatContextType;
  metadata?: Record<string, unknown>;
}

export interface SendMessageResponse {
  sessionId: string;
  // Backend currently returns `message`; keep `response` for backwards compatibility
  message: string;
  response?: string;
  usage?: { inputTokens?: number; outputTokens?: number };
  redacted?: boolean;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  createdAt: string;
}

export interface SessionSummary {
  sessionId: string;
  contextType: ChatContextType;
  createdAt: string;
  endedAt?: string | null;
  lastMessageAt?: string | null;
}

export interface AnalyticsResponse {
  totalSessions: number;
  messagesSent: number;
  tokens: { input: number; output: number };
  period?: string;
}

export const chatbotService = {
  createSession: async (body: CreateSessionRequest) => {
    return await handleApiCall<CreateSessionResponse>(async () => {
      const res = await api.post<CreateSessionResponse>({ url: getApiUrl(endpoint.chatbot.createSession), body, auth: true });
      return res;
    });
  },

  sendMessage: async (body: SendMessageRequest) => {
    // Validate sessionId before sending message
    if (!body.sessionId || body.sessionId === 'undefined' || body.sessionId === 'null') {
      throw new Error('Invalid sessionId: sessionId must be defined and not "undefined"');
    }
    
    try {
      return await handleApiCall<SendMessageResponse>(async () => {
        const res = await api.post<SendMessageResponse>({ url: getApiUrl(endpoint.chatbot.sendMessage), body, auth: true });
        // Some callers type api.post as AxiosResponse<T>, but our axios instance returns data directly via interceptor.
        const data = (res as any)?.data ?? res;
        const responseText = (data as any)?.response ?? (data as any)?.message ?? "";
        return { ...(data as any), response: responseText } as SendMessageResponse;
      });
    } catch (error: any) {
      // Handle 500 errors gracefully - server might be having issues
      if (error?.response?.status === 500) {

        throw new Error('Chatbot service is temporarily unavailable. Please try again later.');
      }
      throw error;
    }
  },

  getHistory: async (sessionId: string) => {
    // Validate sessionId before making API calls
    if (!sessionId || sessionId === 'undefined' || sessionId === 'null') {
      throw new Error('Invalid sessionId: sessionId must be defined and not "undefined"');
    }
    
    // Prefer flat endpoint when available
    try {
      return await handleApiCall<ChatMessage[]>(async () => {
        const flat = await api.get<ChatMessage[]>({ url: getApiUrl(endpoint.chatbot.getHistoryFlat(sessionId)), auth: true });
        const data = (flat as any)?.data ?? flat;
        return data as ChatMessage[];
      });
    } catch {
      // fall back to legacy shape
    }

    // Fallback: backend may return either a flattened array or an object with userMessage[] and botResponse[]
    const responseData = await handleApiCall<unknown>(async () => {
      const res = await api.get<unknown>({ url: getApiUrl(endpoint.chatbot.getHistory(sessionId)), auth: true });
      const data = (res as any)?.data ?? res;
      return data;
    });
    
    // If already an array of ChatMessage, return as-is
    if (Array.isArray(responseData)) {
      return responseData as ChatMessage[];
    }

    // Try to map from ChatBotHistory shape
    const raw = responseData as unknown;
    const maybeContainer = (raw && typeof raw === 'object' && 'data' in (raw as Record<string, unknown>))
      ? (raw as { data?: unknown }).data
      : raw;
    const base = (maybeContainer && typeof maybeContainer === 'object')
      ? (maybeContainer as Record<string, unknown>)
      : {};
    const rawUserMsgs = base['userMessage'];
    const rawBotMsgs = base['botResponse'];
    const userMsgs = Array.isArray(rawUserMsgs)
      ? (rawUserMsgs as { id?: string; content?: string; createdAt?: string }[])
      : [];
    const botMsgs = Array.isArray(rawBotMsgs)
      ? (rawBotMsgs as { id?: string; content?: string; createdAt?: string }[])
      : [];

    const flattened: ChatMessage[] = [];
    const maxLen = Math.max(userMsgs.length, botMsgs.length);
    for (let i = 0; i < maxLen; i++) {
      if (i < userMsgs.length) {
        const u = userMsgs[i];
        flattened.push({
          id: u.id || `u_${i}`,
          role: "user",
          content: u.content || "",
          createdAt: u.createdAt || new Date().toISOString(),
        });
      }
      if (i < botMsgs.length) {
        const b = botMsgs[i];
        flattened.push({
          id: b.id || `a_${i}`,
          role: "assistant",
          content: b.content || "",
          createdAt: b.createdAt || new Date().toISOString(),
        });
      }
    }

    return flattened;
  },

  listSessions: async (limit = 20) => {
    const url = `${getApiUrl(endpoint.chatbot.listSessions)}?limit=${encodeURIComponent(String(limit))}`;
    const res = await handleApiCall<SessionSummary[]>(async () => {
      const r = await api.get<SessionSummary[]>({ url, auth: true });
      return r;
    });
    return Array.isArray(res) ? res : [];
  },

  endSession: async (sessionId: string) => {
    const res = await handleApiCall<{ message?: string }>(async () => {
      const r = await api.post<{ message?: string }>({ url: getApiUrl(endpoint.chatbot.endSession(sessionId)), body: {}, auth: true });
      return r;
    });
    return res;
  },

  getAnalytics: async () => {
    const res = await handleApiCall<AnalyticsResponse>(async () => {
      const r = await api.get<AnalyticsResponse>({ url: getApiUrl(endpoint.chatbot.getAnalytics), auth: true });
      return r;
    });
    return res;
  },
};

export default chatbotService;

